/* eslint-disable no-undef */
/* =============================================================================
 * Mortgages — loan/mortgage bill amortization (ported from LoanCalc / LoanBill).
 * LIVE screen: loans grouped by manager, each previewing the month's QuickBooks
 * Vendor Bill (principal / interest / impound + split carve-outs), plus an editor
 * for the loan terms and splits. Dry run — nothing is sent to QBO.
 * ===========================================================================*/
import React, { useState, useEffect, Fragment } from "react";
import { fmtMoney, fmtShort, Icon, Pill, Avatar, Drawer, Modal, Toggle, initials, mgrColor, useToast } from "./components.jsx";
import { fetchLoans, previewLoanBills, fetchLoanBalances, updateLoan, createLoan, deleteLoan, createLoanBill, fetchLoanSchedule, fetchAccountOrigination, fetchQboConnections, fetchQboStatus } from "./api.js";
import { QboRefPicker } from "./qbo-picker.jsx";

const fmtMonth = (d) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}`;
const COLS = 8;
const LIABILITY_ACCT = ["Liability"];   // principal pays down the loan liability
const ASSET_ACCT = ["Asset"];           // impound funds an escrow asset
const EXPENSE_ACCT = ["Expense"];       // interest is a P&L expense

// Numeric input: no spinner arrows, digits + one decimal only, thousands-formatted with commas when not being edited.
function NumInput({ value, onChange, className = "input", placeholder, decimals }) {
  const [editing, setEditing] = useState(false);
  const fmt = (v) => {
    const s = String(v ?? "");
    if (s === "") return "";
    // Fixed-decimal display (e.g. money): pad to `decimals` places when blurred so 2189.1 shows as 2,189.10.
    if (decimals != null && isFinite(+s)) return (+s).toLocaleString("en-US", { minimumFractionDigits: decimals, maximumFractionDigits: decimals });
    const [i, d] = s.split(".");
    const wi = (i || "").replace(/\B(?=(\d{3})+(?!\d))/g, ",");
    return d != null ? `${wi}.${d}` : wi;
  };
  return (
    <input className={className} type="text" inputMode="decimal" placeholder={placeholder}
      value={editing ? String(value ?? "") : fmt(value)}
      onFocus={() => setEditing(true)} onBlur={() => setEditing(false)}
      onChange={(e) => {
        let v = e.target.value.replace(/[^0-9.]/g, "");
        const p = v.split("."); if (p.length > 2) v = p[0] + "." + p.slice(1).join("");
        onChange(v);
      }} />
  );
}

// Standard amortization payment: P·r / (1 − (1+r)^−n), r = monthly rate. 0 if inputs are incomplete.
function computePayment(P, ratePct, n) {
  if (!(P > 0) || !(n > 0)) return 0;
  const r = (ratePct || 0) / 100 / 12;
  const raw = r === 0 ? P / n : (P * r) / (1 - Math.pow(1 + r, -n));
  return Math.ceil(raw * 100 - 1e-6) / 100;   // match lenders: round the payment up to the next cent (epsilon absorbs float noise on exact cents)
}

// Monthly payment: shows the amount amortization implies (from principal/rate/term) with an Auto toggle.
// Auto keeps it in sync (stored as 0 = "compute on the fly"); off lets the user override a concrete number.
function PaymentField({ principal, ratePct, term, value, onChange }) {
  const auto = computePayment(+principal, +ratePct, +term);
  const isAuto = !(+value > 0);
  return (
    <div className="field" style={{ gridColumn: "1 / -1" }}>
      <div className="between" style={{ marginBottom: 6 }}>
        <label style={{ margin: 0 }}>Monthly payment</label>
        <label className="row" style={{ gap: 7, fontSize: 12, cursor: "pointer" }}>
          <span style={{ color: isAuto ? "var(--color-primary)" : "var(--color-muted)" }}>Auto</span>
          <Toggle on={isAuto} onChange={(v) => onChange(v ? "0" : (auto ? auto.toFixed(2) : ""))} /></label>
      </div>
      {isAuto ? (
        <div>
          <div style={{ fontSize: 24, fontWeight: 700, lineHeight: 1.2 }}>{auto ? fmtMoney(auto) : "—"}<span className="muted" style={{ fontSize: 13, fontWeight: 400 }}> /mo</span></div>
          <div className="muted" style={{ fontSize: 11, marginTop: 2 }}>Updates automatically with principal, rate &amp; term.</div>
        </div>
      ) : (
        <div>
          <NumInput value={value} onChange={onChange} decimals={2} />
          <div className="muted" style={{ fontSize: 11, marginTop: 5 }}>Auto would be {auto ? fmtMoney(auto) : "—"}.</div>
        </div>
      )}
    </div>
  );
}

function Mortgages({ mgr }) {
  const [loans, setLoans] = useState(null);     // array = live · false = failed · null = loading
  const [bills, setBills] = useState({});       // loanId -> LoanBillDto for the selected month
  const [month, setMonth] = useState(fmtMonth(new Date()));
  const [expanded, setExpanded] = useState(null);
  const [editLoan, setEditLoan] = useState(null);
  const [refresh, setRefresh] = useState(0);
  const [newOpen, setNewOpen] = useState(false);
  const [creatingBill, setCreatingBill] = useState(null);   // loanId currently being posted
  const [schedFor, setSchedFor] = useState(null);           // loan whose amortization schedule drawer is open
  const [companies, setCompanies] = useState([]);           // realm → company name, for grouping loans by company
  const [qbo, setQbo] = useState(null);                     // QBO push status → drives the Live/Dry-run pill
  const [balances, setBalances] = useState({});             // loanId → live QBO balance (allow-listed realms only)
  const toast = useToast();

  // Loan bills are drafted on the 1st of each month (due the 15th); show the next occurrence.
  const nextBillRun = (() => { const n = new Date(); const d = new Date(n.getFullYear(), n.getMonth(), 1); if (d <= n) d.setMonth(d.getMonth() + 1); return d; })();
  const createBill = (loan) => {
    setCreatingBill(loan.id);
    createLoanBill(loan.id, `${month}-15`)
      .then((r) => toast({ title: r.pushed ? "Vendor Bill created in QuickBooks" : r.dryRun ? "Dry run — nothing sent to QBO" : "Couldn't post to QuickBooks",
                           body: `${loan.class} → ${loan.vendorName} · ${fmtMoney(r.bill.total)} — ${r.note}`, err: !r.pushed && !r.dryRun }))
      .catch(() => toast({ title: "Couldn't prepare the bill", body: "The API rejected the request." }))
      .finally(() => setCreatingBill(null));
  };

  useEffect(() => {
    let ok = true;
    fetchLoans().then((r) => { if (ok) setLoans(r || []); }).catch(() => { if (ok) setLoans(false); });
    return () => { ok = false; };
  }, [refresh]);

  useEffect(() => {   // company labels (realm → name) so loans group by company, not manager
    let ok = true;
    fetchQboConnections(mgr && mgr !== "all" ? mgr : null).then((c) => { if (ok) setCompanies(c || []); }).catch(() => { if (ok) setCompanies([]); });
    return () => { ok = false; };
  }, [mgr]);

  useEffect(() => { fetchQboStatus().then(setQbo).catch(() => setQbo(null)); }, []);   // push on/off → Live vs Dry-run pill

  useEffect(() => {   // live QBO balance overlay for allow-listed realms; others keep the computed currentBalance
    let ok = true;
    fetchLoanBalances(mgr && mgr !== "all" ? mgr : null)
      .then((list) => { if (!ok) return; const m = {}; (list || []).forEach((b) => { m[b.loanId] = b.balance; }); setBalances(m); })
      .catch(() => { if (ok) setBalances({}); });
    return () => { ok = false; };
  }, [mgr, refresh]);

  useEffect(() => {
    let ok = true;
    previewLoanBills(mgr && mgr !== "all" ? mgr : null, `${month}-15`).then((r) => {   // mid-month payment date (day doesn't affect the split)
      if (!ok) return;
      const map = {}; (r || []).forEach((b) => { map[b.loanId] = b; }); setBills(map);
    }).catch(() => { if (ok) setBills({}); });
    return () => { ok = false; };
  }, [month, refresh, mgr]);

  const usingLive = Array.isArray(loans);
  let list = usingLive ? loans : [];
  if (usingLive && mgr !== "all") list = list.filter((l) => l.managerId === mgr);

  // group by company (the QBO realm each loan bills into), not by manager
  const coName = (realmId) => (companies.find((c) => String(c.realmId) === String(realmId)) || {}).companyName;
  const groups = {};
  list.forEach((l) => { const key = l.realmId || l.managerId; (groups[key] = groups[key] || { key, name: coName(l.realmId) || l.managerName || "—", items: [] }).items.push(l); });
  const groupList = Object.values(groups).sort((a, b) => String(a.name).localeCompare(String(b.name)));
  const groupTotal = (g) => g.items.reduce((s, l) => s + (bills[l.id] ? bills[l.id].total : 0), 0);
  const qboLive = !!(qbo && qbo.pushEnabled && qbo.configured);   // push actually posts to QuickBooks (allow-list still gates per realm)

  const monthTotal = list.reduce((s, l) => s + (bills[l.id] ? bills[l.id].total : 0), 0);
  const monthLabel = new Date(`${month}-01T00:00:00`).toLocaleString("en-US", { month: "long", year: "numeric" });

  if (loans === null)
    return <Shell><div className="card"><div className="empty"><Icon name="refresh" size={18} /> Loading loans…</div></div></Shell>;
  if (loans === false)
    return <Shell><div className="card" style={{ borderLeft: "3px solid var(--color-danger)" }}><div className="empty">Couldn't load loans from the API.</div></div></Shell>;

  const LoanRow = (l) => {
    const b = bills[l.id];
    const open = expanded === l.id;
    // Same rule as the QBO "needs mapping" reconcile count — a loan can't be billed to QuickBooks until all four are set.
    const needsMap = !l.classId || !l.vendorId || !l.principalAccountId || !l.interestAccountId;
    const missing = [!l.classId && "class", !l.vendorId && "vendor", !l.principalAccountId && "principal account", !l.interestAccountId && "interest account"].filter(Boolean).join(", ");
    const splitSum = b ? b.lines.filter((ln) => ln.kind === "Split").reduce((s, ln) => s + ln.amount, 0) : 0;
    const propA = b ? (b.lines.find((ln) => ln.kind === "Split") || {}).class : null;      // fixed side
    const propB = b ? (b.lines.find((ln) => ln.kind === "Interest") || {}).class : null;   // remainder side
    return (
      <Fragment key={l.id}>
        <tr onClick={() => setExpanded(open ? null : l.id)} style={{ cursor: "pointer" }}>
          <td><div className="cell-2"><b style={{ display: "inline-flex", alignItems: "center", gap: 6 }}>{l.class}{needsMap && <span className="pill pill--warn" style={{ fontSize: 9.5 }} title={`Not billable to QuickBooks yet — missing ${missing}. Open this loan to set it.`}>Needs QBO</span>}</b><span>{l.vendorName}</span></div></td>
          <td className="num" title={balances[l.id] != null ? "Live QuickBooks balance" : "Estimated from the amortization schedule"}>{fmtMoney(balances[l.id] ?? l.currentBalance ?? l.startPrincipal)}</td>
          <td className="num">{Number(l.interest)}%</td>
          <td className="num">{b ? fmtMoney(b.principal) : "—"}</td>
          <td className="num">{b ? fmtMoney(b.interest) : "—"}</td>
          <td className="num">{b && b.impound ? fmtMoney(b.impound) : "—"}</td>
          <td className="num strong">{b ? fmtMoney(b.total) : "—"}</td>
          <td><button className="rowmenu"><Icon name={open ? "chevronDown" : "chevron"} size={16} /></button></td>
        </tr>
        {open && b && (
          <tr><td colSpan={COLS} style={{ background: "var(--color-gray-50)", padding: 0 }}>
            <div style={{ padding: "14px 18px" }}>
              <div className="between" style={{ alignItems: "center", marginBottom: 6 }}>
                <div className="section-h" style={{ margin: 0 }}>QuickBooks Bill · {monthLabel} · due {fmtShort(new Date(b.dueDate))}</div>
                <div className="row" style={{ gap: 8 }}>
                  <button className="btn btn--soft btn--sm" onClick={() => setEditLoan(l)}><Icon name="edit" size={13} />Edit loan</button>
                  <button className="btn btn--primary btn--sm" disabled={creatingBill === l.id} onClick={() => createBill(l)}><Icon name="plus" size={13} />{creatingBill === l.id ? "Creating…" : "Create in QBO"}</button>
                </div>
              </div>
              <div style={{ marginBottom: 12 }}>
                <div className="muted" style={{ fontSize: 11.5, marginBottom: 6 }}>This month's payment <b style={{ color: "var(--color-text)" }}>{fmtMoney(b.total)}</b> breaks down as:</div>
                <div style={{ display: "flex", height: 10, borderRadius: 5, overflow: "hidden", background: "var(--color-gray-200)" }}>
                  <div style={{ width: `${b.total ? (b.principal / b.total) * 100 : 0}%`, background: "var(--color-primary)" }} title={`Principal ${fmtMoney(b.principal)}`} />
                  <div style={{ width: `${b.total ? (b.interest / b.total) * 100 : 0}%`, background: "var(--color-warning-text)" }} title={`Interest ${fmtMoney(b.interest)}`} />
                  {b.impound > 0 && <div style={{ width: `${(b.impound / b.total) * 100}%`, background: "var(--color-muted)" }} title={`Impound ${fmtMoney(b.impound)}`} />}
                </div>
                <div className="row" style={{ gap: 16, fontSize: 12, marginTop: 6 }}>
                  <span className="row" style={{ gap: 5 }}><i style={{ width: 8, height: 8, borderRadius: 2, background: "var(--color-primary)", display: "inline-block" }} />Principal <b>{fmtMoney(b.principal)}</b></span>
                  <span className="row" style={{ gap: 5 }}><i style={{ width: 8, height: 8, borderRadius: 2, background: "var(--color-warning-text)", display: "inline-block" }} />Interest <b>{fmtMoney(b.interest)}</b></span>
                  {b.impound > 0 && <span className="row" style={{ gap: 5 }}><i style={{ width: 8, height: 8, borderRadius: 2, background: "var(--color-muted)", display: "inline-block" }} />Impound <b>{fmtMoney(b.impound)}</b></span>}
                </div>
              </div>
              {splitSum > 0 && (
                <div style={{ background: "var(--color-primary-bg)", border: "1px solid var(--color-border-light)", borderRadius: 8, padding: "10px 12px", marginBottom: 12, fontSize: 12.5, lineHeight: 1.55 }}>
                  <b>Shared across two properties.</b> {propA || "This property"} is charged a set <b>{fmtMoney(splitSum)}</b> in interest this month. {propB || "The other property"} covers the rest of the payment — the <b>{fmtMoney(b.principal)}</b> loan paydown plus <b>{fmtMoney(b.interest - splitSum)}</b> interest.
                </div>
              )}
              <table className="lines">
                <thead><tr><th>Account</th><th>Class</th><th>Memo</th><th className="num">Amount</th></tr></thead>
                <tbody>
                  {b.lines.map((ln, i) => (
                    <tr key={i}>
                      <td>{ln.account} <span className="pill pill--muted" style={{ fontSize: 9.5 }}>{ln.kind}</span></td>
                      <td className="muted">{ln.class || "—"}</td>
                      <td className="muted">{ln.desc}</td>
                      <td className="num">{fmtMoney(ln.amount)}</td>
                    </tr>
                  ))}
                </tbody>
                <tfoot><tr><td colSpan={3}>Bill total → {l.vendorName}</td><td className="num">{fmtMoney(b.total)}</td></tr></tfoot>
              </table>
              <div style={{ marginTop: 10 }}>
                <button className="btn btn--ghost btn--sm" onClick={() => setSchedFor(l)}><Icon name="template" size={13} />View amortization schedule</button>
              </div>
            </div>
          </td></tr>
        )}
      </Fragment>
    );
  };

  return (
    <div className="page">
      <div className="page__head">
        <div><h1>Mortgages</h1><p>{list.length} active loans · {groupList.length} compan{groupList.length === 1 ? "y" : "ies"} · amortized principal / interest / impound → QuickBooks Bills · Live (API)</p></div>
        <div className="right" style={{ gap: 10 }}>
          <div className="field" style={{ margin: 0 }}><input className="input" type="month" value={month} onChange={(e) => setMonth(e.target.value)} /></div>
          <button className="btn btn--primary" onClick={() => setNewOpen(true)}><Icon name="plus" size={16} />New loan</button>
        </div>
      </div>

      <div className="card" style={{ background: "var(--accent)", color: "#fff", marginBottom: 16 }}>
        <div className="between">
          <div>
            <div style={{ fontSize: 12.5, opacity: .85, fontWeight: 600, textTransform: "uppercase", letterSpacing: ".5px" }}>{monthLabel} · total due</div>
            <div style={{ fontSize: 34, fontWeight: 700, lineHeight: 1.15 }}>{fmtMoney(monthTotal)}</div>
            <div style={{ opacity: .85, fontSize: 12.5 }}>{list.length} vendor bills across {groupList.length} compan{groupList.length === 1 ? "y" : "ies"}</div>
          </div>
          <div style={{ display: "flex", flexDirection: "column", alignItems: "flex-end", gap: 8 }}>
            {qboLive ? <Pill kind="warn">Live · {qbo.environment}</Pill> : <Pill kind="muted">Dry run · not sent to QBO</Pill>}
            <div style={{ opacity: .85, fontSize: 12.5 }} title="Loan bills are drafted on the 1st of each month (due the 15th). Auto-posting turns on when QuickBooks push is enabled.">Next bill run · {fmtShort(nextBillRun)}</div>
          </div>
        </div>
      </div>

      <div className="tbl-wrap">
        <table className="tbl">
          <thead>
            <tr>
              <th>Loan / Vendor</th><th className="num">Current bal.</th><th className="num">Rate</th>
              <th className="num">Principal</th><th className="num">Interest</th><th className="num">Impound</th><th className="num">Payment</th><th style={{ width: 30 }}></th>
            </tr>
          </thead>
          <tbody>
            {groupList.map((g) => (
              <Fragment key={g.key}>
                <tr className="grouprow"><td colSpan={COLS}>
                  <span className="row" style={{ gap: 8 }}>
                    <Avatar id={g.key} label={initials(g.name)} size={18} round={4} />
                    {g.name}
                    <span className="muted" style={{ textTransform: "none", letterSpacing: 0, fontWeight: 500 }}>· {g.items.length} loan{g.items.length !== 1 ? "s" : ""} · {fmtMoney(groupTotal(g))}/mo</span>
                  </span>
                </td></tr>
                {g.items.map((l) => LoanRow(l))}
              </Fragment>
            ))}
            {!list.length && <tr><td colSpan={COLS}><div className="empty">No active loans{mgr !== "all" ? " for this manager" : ""}.</div></td></tr>}
          </tbody>
        </table>
      </div>

      <LoanEditor loan={editLoan} creating={newOpen} mgr={mgr}
        onClose={() => { setEditLoan(null); setNewOpen(false); }}
        onSaved={() => setRefresh((n) => n + 1)} onSchedule={() => setSchedFor(editLoan)} />
      <AmortizationDrawer loan={schedFor} onClose={() => setSchedFor(null)} />
    </div>
  );
}

function Shell({ children }) {
  return (
    <div className="page">
      <div className="page__head"><div><h1>Mortgages</h1><p>Loan / mortgage bills</p></div></div>
      {children}
    </div>
  );
}

// Edit a loan's terms + interest splits. Saving recomputes the monthly bill.
function LoanEditor({ loan, creating, mgr, onClose, onSaved, onSchedule }) {
  const toast = useToast();
  const [f, setF] = useState(null);
  const [saving, setSaving] = useState(false);
  const [confirmDel, setConfirmDel] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [companies, setCompanies] = useState([]);   // create mode: QBO companies to pick from
  const [orig, setOrig] = useState(null);           // create mode: origination pulled from the picked loan account
  const [origBusy, setOrigBusy] = useState(false);
  useEffect(() => {
    setConfirmDel(false); setOrig(null);
    if (creating) {
      fetchQboConnections(mgr && mgr !== "all" ? mgr : null).then((c) => setCompanies(c || [])).catch(() => setCompanies([]));
      setF({
        realmId: "", class: "", principalClass: "", vendorName: "", classId: null, principalClassId: null, vendorId: null,
        startPrincipal: "", startMonth: `${new Date().getFullYear()}-01`, interest: "3", term: "360", payment: "", impoundAmount: 0,
        principalAccount: "", interestAccount: "", impoundAccount: "",
        principalAccountId: null, interestAccountId: null, impoundAccountId: null,
        impound: false, active: true, xcol: false, fixedInterestA: 0,
        principalClassAccount: "", principalClassAccountId: null, principalToOther: false,
      });
      return;
    }
    if (!loan) { setF(null); return; }
    setF({
      class: loan.class, principalClass: loan.principalClass || "", vendorName: loan.vendorName,
      classId: loan.classId || null, principalClassId: loan.principalClassId || null, vendorId: loan.vendorId || null,
      startPrincipal: loan.startPrincipal, startMonth: String(loan.startMonth).slice(0, 7),
      interest: loan.interest, term: loan.term, payment: loan.payment, impoundAmount: loan.impoundAmount,
      principalAccount: loan.principalAccount, interestAccount: loan.interestAccount, impoundAccount: loan.impoundAccount || "",
      principalAccountId: loan.principalAccountId || null, interestAccountId: loan.interestAccountId || null, impoundAccountId: loan.impoundAccountId || null,
      impound: !!(+loan.impoundAmount > 0 || loan.impoundAccountId), active: loan.active,
      xcol: !!(loan.principalClass && loan.principalClass !== loan.class) || !!(loan.splits && loan.splits.length),
      fixedInterestA: (loan.splits && loan.splits[0]) ? loan.splits[0].amount : 0,
      principalClassAccount: loan.principalClassAccount || "", principalClassAccountId: loan.principalClassAccountId || null,
      principalToOther: !!(loan.principalClassAccountId || loan.principalClassAccount),
    });
  }, [loan && loan.id, creating]);
  if ((!loan && !creating) || !f) return null;

  const set = (k, v) => setF((x) => ({ ...x, [k]: v }));

  const submit = () => {
    if (creating && !f.realmId) { toast({ title: "Company required", body: "Pick the QuickBooks company this loan bills into." }); return; }
    if (!f.class.trim() || !f.vendorName.trim()) { toast({ title: "Missing fields", body: "Class and vendor are required." }); return; }
    setSaving(true);
    const payload = {
      class: f.class, principalClass: f.xcol ? (f.principalClass || null) : null, vendorName: f.vendorName,
      classId: f.classId, principalClassId: f.xcol ? f.principalClassId : null, vendorId: f.vendorId,
      startPrincipal: +f.startPrincipal || 0, startMonth: `${f.startMonth}-01`, interest: +f.interest || 0, term: +f.term || 360,
      payment: +f.payment || 0, impoundAmount: f.impound ? +f.impoundAmount : 0,
      principalAccount: f.principalAccount || "Mortgage Principal", interestAccount: f.interestAccount || "Mortgage Interest",
      impoundAccount: f.impound ? (f.impoundAccount || null) : null, active: f.active,
      principalAccountId: f.principalAccountId, interestAccountId: f.interestAccountId, impoundAccountId: f.impound ? f.impoundAccountId : null,
      principalClassAccount: (f.xcol && f.principalToOther) ? (f.principalClassAccount || null) : null,
      principalClassAccountId: (f.xcol && f.principalToOther) ? (f.principalClassAccountId || null) : null,
      splits: f.xcol && +f.fixedInterestA > 0
        ? [{ class: f.class, account: f.interestAccount, amount: +f.fixedInterestA, memo: "Interest", classId: f.classId || null, accountId: f.interestAccountId || null }]
        : [],
    };
    const req = creating ? createLoan(f.realmId, payload) : updateLoan(loan.id, payload);
    req
      .then(() => { setSaving(false); onSaved(); onClose(); toast({ title: creating ? "Loan created" : "Loan saved", body: creating ? `${f.class} added — monthly bill ready.` : `${f.class} updated — bill recomputed.` }); })
      .catch(() => { setSaving(false); toast({ title: creating ? "Create failed" : "Save failed", body: "The API rejected the loan." }); });
  };

  // Picking the loan's liability account pulls its origination (opening balance + first month) when creating.
  // Only prefill start principal + month if you haven't entered a start principal yet — never clobber a typed value.
  const pickPrincipal = ({ qboId, name }) => {
    setF((x) => ({ ...x, principalAccountId: qboId, principalAccount: name }));
    if (creating && qboId && f.realmId) {
      setOrig(null); setOrigBusy(true);
      fetchAccountOrigination(f.realmId, qboId)
        .then((o) => {
          setOrigBusy(false);
          if (!o || !o.startMonth) return;
          setOrig(o);
          setF((x) => (+x.startPrincipal > 0 ? x : { ...x, startPrincipal: String(o.openingBalance), startMonth: o.startMonth }));
        })
        .catch(() => setOrigBusy(false));
    }
  };

  const onDelete = () => {
    setDeleting(true);
    deleteLoan(loan.id)
      .then(() => { setDeleting(false); onSaved(); onClose(); toast({ title: "Loan deleted", body: `${loan.class} was removed.` }); })
      .catch(() => { setDeleting(false); setConfirmDel(false); toast({ title: "Delete failed", body: "Couldn't delete this loan." }); });
  };

  const F = ({ label, k, type = "text", step, full }) => (
    <div className="field" style={full ? { gridColumn: "1 / -1" } : undefined}><label>{label}</label>
      {type === "number"
        ? <NumInput value={f[k]} onChange={(v) => set(k, v)} />
        : <input className="input" type={type} step={step} value={f[k]} onChange={(e) => set(k, e.target.value)} />}</div>
  );
  const realm = creating ? f.realmId : loan.realmId;   // QBO company this loan bills into; drives the pickers below
  // A field can hold a saved NAME with no QuickBooks id (migrated/typed) — it looks filled but isn't mapped, which
  // is exactly what the "Needs QBO" badge counts. Flag those so it's obvious which picker to re-select.
  const unlinked = (nameKey, idKey) => !!f[nameKey] && !f[idKey];
  const linkBadge = (nameKey, idKey) => unlinked(nameKey, idKey)
    ? <span title="Shows a saved name but isn't linked to a QuickBooks record — pick it from the list to map it." style={{ color: "var(--color-warning-text)", fontSize: 10, fontWeight: 700, marginLeft: 6 }}>● not linked</span>
    : null;
  const qField = (label, type, nameKey, idKey, subTypes) => (
    <div className="field"><label>{label}{linkBadge(nameKey, idKey)}</label>
      <QboRefPicker realmId={realm} type={type} value={f[idKey]} name={f[nameKey]} subTypes={subTypes} placeholder={`Select ${type}…`}
        onChange={({ qboId, name }) => setF((x) => ({ ...x, [idKey]: qboId, [nameKey]: name }))} /></div>
  );

  return (
    <Drawer open={!!loan || creating} onClose={onClose} wide>
      <div className="drawer__head">
        <div className="av av-sq" style={{ background: creating ? "var(--color-primary)" : mgrColor(loan.managerId) }}><Icon name="landmark" size={20} /></div>
        <div style={{ flex: 1 }}><h2>{creating ? "New loan" : loan.class}</h2><div className="sub">{creating ? "Set up the mortgage and how it posts to QuickBooks" : `${loan.vendorName} · ${loan.managerName}`}</div></div>
        {!creating && <button className="btn btn--soft btn--sm" onClick={onSchedule} style={{ marginRight: 8 }}><Icon name="template" size={13} />Amortization</button>}
        <button className="drawer__close" onClick={onClose}>&times;</button>
      </div>

      <div className="drawer__body">
        {creating && (
          <div className="field" style={{ marginBottom: 12 }}><label>Company (QuickBooks)</label>
            <select className="select" value={f.realmId} onChange={(e) => setF((x) => ({ ...x, realmId: e.target.value, classId: null, class: "", vendorId: null, vendorName: "", principalAccountId: null, principalAccount: "", interestAccountId: null }))}>
              <option value="">Select company…</option>
              {companies.map((c) => <option key={c.realmId} value={c.realmId}>{c.companyName || `Realm ${c.realmId}`}{c.connected ? "" : " · disconnected"}</option>)}
            </select>
          </div>
        )}
        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 }}>
          {qField("Class (property)", "Class", "class", "classId")}
          {qField("Vendor", "Vendor", "vendorName", "vendorId")}
          <div className="field"><label>Principal account (liability){linkBadge("principalAccount", "principalAccountId")}</label>
            <QboRefPicker realmId={realm} type="Account" subTypes={LIABILITY_ACCT} value={f.principalAccountId} name={f.principalAccount} placeholder="Select Account…"
              onChange={pickPrincipal} /></div>
          {qField("Interest account", "Account", "interestAccount", "interestAccountId", EXPENSE_ACCT)}
        </div>
        {creating && (origBusy || orig) && (
          <div className="muted" style={{ fontSize: 11.5, marginTop: 6 }}>
            {origBusy ? "Reading balance from QuickBooks…" : `Current balance ${fmtMoney(orig.currentBalance)} · opened ${fmtMo(orig.firstTxnDate)} — start principal & month prefilled from its ledger.`}
          </div>
        )}
        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12, marginTop: 12 }}>
          {F({ label: "Start principal", k: "startPrincipal", type: "number" })}
          <div className="field"><label>Start month</label><input className="input" type="month" value={f.startMonth} onChange={(e) => set("startMonth", e.target.value)} /></div>
          {F({ label: "Interest rate (%)", k: "interest", type: "number" })}
          {F({ label: "Term (months)", k: "term", type: "number" })}
          <PaymentField principal={f.startPrincipal} ratePct={f.interest} term={f.term} value={f.payment} onChange={(v) => set("payment", v)} />
        </div>
        <div className="between" style={{ padding: "10px 0" }}>
          <div><span style={{ fontSize: 13, fontWeight: 600 }}>Escrow / impound</span><div className="muted" style={{ fontSize: 11.5, marginTop: 2 }}>Collect impound (taxes / insurance) each month and post it to an escrow asset account.</div></div>
          <Toggle on={f.impound} onChange={(v) => set("impound", v)} />
        </div>
        {f.impound && (
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 }}>
            {F({ label: "Monthly impound amount", k: "impoundAmount", type: "number" })}
            {qField("Impound account", "Account", "impoundAccount", "impoundAccountId", ASSET_ACCT)}
          </div>
        )}
        <div className="between" style={{ padding: "10px 0" }}><span style={{ fontSize: 13, fontWeight: 600 }}>Active</span><Toggle on={f.active} onChange={(v) => set("active", v)} /></div>

        <div className="section-h">Split across two properties</div>
        <div style={{ background: "var(--color-gray-50)", border: "1px solid var(--color-border-light)", borderRadius: 8, padding: "12px 14px" }}>
          <div className="between" style={{ alignItems: "flex-start", gap: 12 }}>
            <div>
              <div style={{ fontSize: 13, fontWeight: 600 }}>Part of this loan paid for another property</div>
              <div className="muted" style={{ fontSize: 11.5, marginTop: 2, lineHeight: 1.5 }}>Charge a set amount to {f.class || "this property"} each month; everything else goes to the other property.</div>
            </div>
            <Toggle on={f.xcol} onChange={(v) => set("xcol", v)} />
          </div>
          {f.xcol && (
            <div style={{ marginTop: 14, display: "grid", gap: 12 }}>
              <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 }}>
                <div className="field"><label>Interest charged to {f.class || "this property"} / mo</label>
                  <NumInput className="input mono" value={f.fixedInterestA} onChange={(v) => set("fixedInterestA", v)} /></div>
                {qField("Other property", "Class", "principalClass", "principalClassId")}
              </div>
              <div className="between" style={{ padding: "2px 0" }}>
                <div><span style={{ fontSize: 13, fontWeight: 600 }}>Pay principal to {f.principalClass || "the other property"}'s mortgage</span>
                  <div className="muted" style={{ fontSize: 11.5, marginTop: 2, lineHeight: 1.5 }}>Post the paydown to that property's own liability account instead of the primary mortgage — keeps it off this property's books.</div></div>
                <Toggle on={f.principalToOther} onChange={(v) => set("principalToOther", v)} />
              </div>
              {f.principalToOther && qField(`${f.principalClass || "Other property"}'s mortgage account`, "Account", "principalClassAccount", "principalClassAccountId", LIABILITY_ACCT)}
              <div className="muted" style={{ fontSize: 11.5, lineHeight: 1.55 }}>
                Each month, {f.class || "this property"} is charged {fmtMoney(+f.fixedInterestA || 0)} in interest. {f.principalClass || "The other property"} covers the rest — the loan paydown{f.principalToOther ? ` (posted to ${f.principalClassAccount || "its mortgage"})` : ""} plus the remaining interest.
              </div>
            </div>
          )}
        </div>
      </div>

      <div className="drawer__foot">
        {!creating && (confirmDel ? (
          <span className="row" style={{ gap: 8 }}>
            <span className="muted" style={{ fontSize: 12 }}>Delete this loan?</span>
            <button className="btn btn--ghost btn--sm" onClick={() => setConfirmDel(false)}>No</button>
            <button className="btn btn--sm" style={{ background: "var(--color-danger)", color: "#fff" }} disabled={deleting} onClick={onDelete}>{deleting ? "Deleting…" : "Yes, delete"}</button>
          </span>
        ) : (
          <button className="btn btn--ghost btn--sm" style={{ color: "var(--color-danger)" }} onClick={() => setConfirmDel(true)}>Delete</button>
        ))}
        <span className="muted" style={{ fontSize: 12, flex: 1, textAlign: "center" }}>{creating ? "You can fine-tune accounts & splits anytime." : "Changes recompute the monthly bill."}</span>
        <button className="btn btn--ghost" onClick={onClose}>Cancel</button>
        <button className="btn btn--primary" disabled={saving} onClick={submit}>{saving ? (creating ? "Creating…" : "Saving…") : (creating ? "Create loan" : "Save loan")}</button>
      </div>
    </Drawer>
  );
}

// ---- Amortization schedule drawer -------------------------------------------
const fmtMo = (d) => new Date(d).toLocaleDateString("en-US", { month: "short", year: "numeric" });

function SummaryCard({ label, value, sub }) {
  return (
    <div className="card" style={{ padding: 12, boxShadow: "none", border: "1px solid var(--color-border-light)" }}>
      <div className="muted" style={{ fontSize: 10.5, textTransform: "uppercase", fontWeight: 600, letterSpacing: ".4px" }}>{label}</div>
      <div style={{ fontSize: 18, fontWeight: 700, marginTop: 3, fontVariantNumeric: "tabular-nums" }}>{value}</div>
      {sub && <div className="muted" style={{ fontSize: 11 }}>{sub}</div>}
    </div>
  );
}

// Principal-vs-interest crossover over the life of the loan (the classic amortization shape).
function ScheduleChart({ rows }) {
  if (!rows || rows.length < 2) return null;
  const w = 660, h = 120, pad = 6, n = rows.length;
  const maxV = Math.max(...rows.map((r) => Math.max(r.principal, r.interest))) || 1;
  const x = (i) => pad + (i / (n - 1)) * (w - 2 * pad);
  const y = (v) => h - pad - (v / maxV) * (h - 2 * pad);
  const path = (key) => rows.map((r, i) => `${i ? "L" : "M"}${x(i).toFixed(1)} ${y(r[key]).toFixed(1)}`).join(" ");
  return (
    <div style={{ marginBottom: 14 }}>
      <svg viewBox={`0 0 ${w} ${h}`} style={{ width: "100%", height: 110, display: "block", border: "1px solid var(--color-border-light)", borderRadius: 8, background: "var(--color-gray-50)" }}>
        <path d={path("interest")} fill="none" stroke="var(--color-warning-text)" strokeWidth="1.8" />
        <path d={path("principal")} fill="none" stroke="var(--color-primary)" strokeWidth="1.8" />
      </svg>
      <div className="row" style={{ gap: 16, fontSize: 11.5, justifyContent: "center", marginTop: 6 }}>
        <span className="row" style={{ gap: 5 }}><i style={{ width: 8, height: 8, borderRadius: 2, background: "var(--color-primary)", display: "inline-block" }} />Principal (grows)</span>
        <span className="row" style={{ gap: 5 }}><i style={{ width: 8, height: 8, borderRadius: 2, background: "var(--color-warning-text)", display: "inline-block" }} />Interest (shrinks)</span>
      </div>
    </div>
  );
}

function AmortizationDrawer({ loan, onClose }) {
  const [rows, setRows] = useState(null);   // array · false = failed · null = loading
  useEffect(() => {
    if (!loan) { setRows(null); return; }
    let ok = true;
    fetchLoanSchedule(loan.id).then((r) => { if (ok) setRows(r || []); }).catch(() => { if (ok) setRows(false); });
    return () => { ok = false; };
  }, [loan && loan.id]);
  if (!loan) return null;
  const list = Array.isArray(rows) ? rows : [];
  const nowKey = new Date().toISOString().slice(0, 7);
  const view = list.map((r, i) => ({ ...r, n: i + 1 })).filter((r) => r.month.slice(0, 7) >= nowKey);   // this month forward
  const totalInterest = view.reduce((s, r) => s + r.interest, 0);
  const payment = view.length ? view[0].payment : 0;
  const payoff = view.length ? view[view.length - 1].month : null;
  return (
    <Drawer open={!!loan} onClose={onClose} wide>
      <div className="drawer__head">
        <div className="av av-sq" style={{ background: mgrColor(loan.managerId) }}><Icon name="template" size={20} /></div>
        <div style={{ flex: 1 }}>
          <h2>{loan.class} — amortization</h2>
          <div className="sub">{loan.vendorName} · {fmtMoney(loan.startPrincipal)} @ {Number(loan.interest)}% · {loan.term} mo</div>
        </div>
        <button className="drawer__close" onClick={onClose}>&times;</button>
      </div>
      <div className="drawer__body">
        {rows === null ? <div className="empty"><Icon name="refresh" size={16} /> Loading schedule…</div>
         : rows === false ? <div className="empty">Couldn't load the schedule.</div>
         : view.length === 0 ? <div className="empty">This loan is fully paid off.</div>
         : (
          <>
            <div style={{ display: "grid", gridTemplateColumns: "repeat(3,1fr)", gap: 10, marginBottom: 14 }}>
              <SummaryCard label="Monthly payment" value={fmtMoney(payment)} />
              <SummaryCard label="Interest remaining" value={fmtMoney(totalInterest)} />
              <SummaryCard label="Payoff" value={payoff ? fmtMo(payoff) : "—"} sub={`${view.length} payments left`} />
            </div>
            <ScheduleChart rows={view} />
            <div className="section-h">Schedule</div>
            <div className="tbl-wrap" style={{ boxShadow: "none", border: "1px solid var(--color-border-light)", maxHeight: 360, overflowY: "auto" }}>
              <table className="lines">
                <thead><tr><th style={{ paddingLeft: 12 }}>#</th><th>Month</th><th className="num">Payment</th><th className="num">Principal</th><th className="num">Interest</th><th className="num">Balance</th></tr></thead>
                <tbody>
                  {view.map((r) => (
                    <tr key={r.n} style={r.month.slice(0, 7) === nowKey ? { background: "var(--color-primary-bg)", fontWeight: 600 } : undefined}>
                      <td className="muted" style={{ paddingLeft: 12 }}>{r.n}</td>
                      <td>{fmtMo(r.month)}</td>
                      <td className="num">{fmtMoney(r.payment)}</td>
                      <td className="num">{fmtMoney(r.principal)}</td>
                      <td className="num">{fmtMoney(r.interest)}</td>
                      <td className="num">{fmtMoney(r.balance)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <div className="muted" style={{ fontSize: 12, marginTop: 12, lineHeight: 1.55 }}>
              Every month you pay {fmtMoney(payment)}. Early on, most of it is interest; over time more goes toward paying down the loan, until it reaches zero. The current month is highlighted.
            </div>
          </>
        )}
      </div>
    </Drawer>
  );
}

export { Mortgages };
